import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface GuildRole {
  id: string;
  name: string;
  color: number;
  position: number;
}

export interface GuildMember {
  id: string;
  username: string;
  rawUsername: string;
  displayName: string;
  avatarUrl: string | null;
  bot: boolean;
}

/** Names for `<@&id>` / `<@id>` in previews (lib/discordText.tsx). */
export interface MentionNames {
  roles: Map<string, { name: string; color: number }>;
  users: Map<string, string>;
}

/** Same query keys as RolePicker / UserPicker, so the caches are shared. */
export function useGuildRoles(guildId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['guild-roles', guildId],
    queryFn: () => api<{ roles: GuildRole[] }>(`/api/guilds/${guildId}/roles`),
    enabled: !!guildId && enabled,
    staleTime: 60_000,
  });
}

export function useGuildMembers(guildId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['members', guildId],
    queryFn: () => api<{ members: GuildMember[] }>(`/api/guilds/${guildId}/members`),
    enabled: !!guildId && enabled,
    staleTime: 60_000,
  });
}

/**
 * Role / member names for the mentions in `texts` — loads roles and members
 * only when the texts actually contain such mentions.
 */
export function useMentionNames(guildId: string | undefined, texts: (string | undefined)[]): MentionNames {
  const joined = texts.join(' ');
  const roles = useGuildRoles(guildId, /<@&\d+>/.test(joined));
  const members = useGuildMembers(guildId, /<@!?\d+>/.test(joined));
  return useMemo(
    () => ({
      roles: new Map((roles.data?.roles ?? []).map((r) => [r.id, { name: r.name, color: r.color }])),
      users: new Map((members.data?.members ?? []).map((m) => [m.id, m.displayName || m.username])),
    }),
    [roles.data, members.data]
  );
}
